import { useState, useEffect, useCallback } from 'react';
import { getSleepLogsLastWeek, createSleepLog, updateSleepLog, deleteSleepLog } from '@/lib/firebase/queries/sleep';
import type { SleepLog, CreateSleepLogParams, UpdateSleepLogParams } from '@/lib/firebase/queries/sleep';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export function useSleepLogs() {
  const [sleepLogs, setSleepLogs] = useState<SleepLog[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  const fetchSleepLogs = useCallback(async () => {
    if (!currentUser?.uid) {
      setSleepLogs([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const logs = await getSleepLogsLastWeek(currentUser.uid);
      setSleepLogs(logs);
    } catch (error) {
      console.error('Error fetching sleep logs:', error);
      toast.error(t('sleep.error.fetch') || 'Failed to fetch sleep logs');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, t]);

  useEffect(() => {
    fetchSleepLogs();
  }, [fetchSleepLogs]);

  const addSleepLog = async (params: Omit<CreateSleepLogParams, 'userId'>) => {
    if (!currentUser?.uid) return null;

    try {
      const log = await createSleepLog({
        ...params,
        userId: currentUser.uid
      });
      await fetchSleepLogs();
      toast.success(t('sleep.success.created') || 'Sleep log created successfully');
      return log;
    } catch (error) {
      console.error('Error creating sleep log:', error);
      toast.error(t('sleep.error.create') || 'Failed to create sleep log');
      return null;
    }
  };

  const editSleepLog = async (sleepLogId: string, params: UpdateSleepLogParams) => {
    try {
      await updateSleepLog(sleepLogId, params);
      await fetchSleepLogs();
      toast.success(t('sleep.success.updated') || 'Sleep log updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating sleep log:', error);
      toast.error(t('sleep.error.update') || 'Failed to update sleep log');
      return false;
    }
  };

  const removeSleepLog = async (sleepLogId: string) => {
    try {
      await deleteSleepLog(sleepLogId);
      await fetchSleepLogs();
      toast.success(t('sleep.success.deleted') || 'Sleep log deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting sleep log:', error);
      toast.error(t('sleep.error.delete') || 'Failed to delete sleep log');
      return false;
    }
  };

  return {
    sleepLogs,
    loading,
    addSleepLog,
    editSleepLog,
    removeSleepLog,
    refreshSleepLogs: fetchSleepLogs,
  };
}
